import React, { useState, useEffect, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import { activityApi } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { LoadingButton } from '../common/LoadingComponents';
import { getErrorMessage } from '../../utils/helpers';
import { useKeyboardNavigation } from '../../hooks/useKeyboardNavigation';
import { generateId } from '../../utils/accessibility';
import RichTextEditor from '../common/RichTextEditor';

function ActivityForm({ activity, onSubmit, onCancel }) {
  const isEditing = !!activity;
  const toast = useToast();
  const titleInputRef = useRef(null);
  const [formId] = useState(() => generateId('activity-form'));
  
  const [formData, setFormData] = useState({
    type: 'announcement',
    title: '',
    body: '',
    tags: [], // Changed from string to array
    eventDate: '',
    links: ''
  });
  
  const [tagInput, setTagInput] = useState('');
  
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activity) {
      setFormData({
        type: activity.type || 'announcement',
        title: activity.title || '',
        body: activity.body || '',
        tags: activity.tags || [], // Keep as array
        eventDate: activity.eventDate || '',
        links: (activity.links || []).join('\n')
      });
    }
  }, [activity]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag) && formData.tags.length < 10) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const getPlainText = (html) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent.trim();
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else if (formData.title.length > 200) {
      errors.title = 'Title must be 200 characters or less';
    }
    
    if (!formData.body.trim() || getPlainText(formData.body).length === 0) {
      errors.body = 'Body is required';
    } else if (formData.body.length > 5000) {
      errors.body = 'Body must be 5000 characters or less';
    }
    
    if (formData.type === 'event' && formData.eventDate) {
      try {
        const eventDate = new Date(formData.eventDate);
        if (isNaN(eventDate.getTime())) {
          errors.eventDate = 'Please enter a valid date';
        }
      } catch {
        errors.eventDate = 'Please enter a valid date';
      }
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const prepareActivityData = () => {
    const data = {
      type: formData.type,
      title: formData.title.trim(),
      body: formData.body.trim()
    };
    
    // Process tags (already an array)
    if (formData.tags.length > 0) {
      data.tags = formData.tags.slice(0, 10); // Limit to 10 tags
    }
    
    // Process links
    if (formData.links.trim()) {
      data.links = formData.links
        .split('\n')
        .map(link => link.trim())
        .filter(link => link.length > 0)
        .slice(0, 5); // Limit to 5 links
    }
    
    // Event date
    if (formData.type === 'event' && formData.eventDate) {
      data.eventDate = new Date(formData.eventDate).toISOString();
    }
    
    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.warning('Please fix the errors before submitting.');
      return;
    }
    
    setLoading(true);
    
    try {
      const activityData = prepareActivityData();
      let response;
      
      if (isEditing) {
        response = await activityApi.updateActivity(activity._id, activityData);
      } else {
        response = await activityApi.createActivity(activityData);
      }
      
      onSubmit(response.activity);
    } catch (err) {
      console.error('Failed to save activity:', err);
      toast.error(getErrorMessage(err) || 'Unable to save post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Keyboard navigation - Escape to close
  useKeyboardNavigation({
    'Escape': (e) => {
      if (!loading) {
        e.preventDefault();
        onCancel();
      }
    }
  }, [loading, onCancel]);

  // Focus title input when form opens
  useEffect(() => {
    if (titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, []);

  return (
    <div 
      className="activity-form-overlay" 
      role="dialog" 
      aria-modal="true"
      aria-labelledby={`${formId}-title`}
    >
      <FocusLock returnFocus>
        <div className="activity-form-modal">
          <div className="activity-form-header">
            <h2 id={`${formId}-title`}>{isEditing ? 'Edit Community Post' : 'Create New Community Post'}</h2>
            <button
              onClick={onCancel}
              className="modal-close-btn"
              disabled={loading}
              aria-label="Close form"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="activity-form" noValidate>
            <div className="form-group">
              <label htmlFor="type">Type *</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="form-select"
                required
                aria-required="true"
              >
                <option value="announcement">Announcement</option>
                <option value="event">Event</option>
                <option value="discussion">Discussion</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                ref={titleInputRef}
              value={formData.title}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter a clear, descriptive title"
              maxLength="200"
              required
              aria-required="true"
              aria-invalid={!!fieldErrors.title}
              aria-describedby={fieldErrors.title ? `${formId}-title-error` : `${formId}-title-counter`}
            />
            <div id={`${formId}-title-counter`} className="character-counter" aria-live="polite">
              {formData.title.length}/200 characters
            </div>
            {fieldErrors.title && (
              <span id={`${formId}-title-error`} className="field-error" role="alert">
                {fieldErrors.title}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="body">Content *</label>
            <RichTextEditor
              id="body"
              value={formData.body}
              onChange={(value) => {
                setFormData(prev => ({ ...prev, body: value }));
                // Clear field error when user starts typing
                if (fieldErrors.body) {
                  setFieldErrors(prev => ({ ...prev, body: null }));
                }
              }}
              placeholder="Share your announcement, event details, or discussion topic..."
              maxLength={5000}
              error={fieldErrors.body}
            />
          </div>

          {formData.type === 'event' && (
            <div className="form-group">
              <label htmlFor="eventDate">Event Date</label>
              <input
                type="datetime-local"
                id="eventDate"
                name="eventDate"
                value={formData.eventDate}
                onChange={handleInputChange}
                className="form-input"
                aria-invalid={!!fieldErrors.eventDate}
                aria-describedby={fieldErrors.eventDate ? `${formId}-eventDate-error` : undefined}
              />
              {fieldErrors.eventDate && (
                <span className="field-error">{fieldErrors.eventDate}</span>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="tags">Tags</label>
            <div className="tags-input-container">
              <input
                type="text"
                id="tags"
                value={tagInput}
                onChange={handleTagInputChange}
                onKeyDown={handleTagInputKeyDown}
                placeholder="Add tags (press Enter or comma to add)"
                className="tags-input"
              />
              <button type="button" onClick={addTag} className="btn btn-sm btn-secondary">
                Add
              </button>
            </div>
            
            {formData.tags.length > 0 && (
              <div className="tags-display">
                {formData.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="tag-remove"
                      aria-label={`Remove ${tag} tag`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            
            <small className="form-help">
              {formData.tags.length}/10 tags
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="links">Resource Links</label>
            <textarea
              id="links"
              name="links"
              value={formData.links}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder="Add relevant links (one per line, max 5)"
              rows="3"
            />
            <small className="form-help">Enter one link per line. Include full URLs starting with http:// or https://</small>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary"
              disabled={loading}
              aria-label="Cancel and close form"
            >
              Cancel
            </button>
            <LoadingButton
              type="submit"
              className="btn btn-primary"
              loading={loading}
              loadingText={isEditing ? 'Updating...' : 'Creating...'}
              aria-label={isEditing ? 'Update post' : 'Create post'}
            >
              {isEditing ? 'Update Post' : 'Create Post'}
            </LoadingButton>
          </div>
        </form>
        </div>
      </FocusLock>
    </div>
  );
}

export default ActivityForm;